import React, { useEffect, useId, useState } from 'react';

import { Queries } from '@aline/common';
import { InputLabel, MenuItem, Typography } from '@mui/material';
import { ToolbarInput, ToolbarSelect } from 'src/components';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  boundsColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  boundsGrid: {
    columnGap: theme.spacing(2),
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    rowGap: theme.spacing(1),
  },
  inputLabel: {
    ...theme.appTypography.fieldLabel,
  },
  sectionLabel: {
    ...theme.appTypography.fieldLabel,
    marginBottom: theme.spacing(0.5),
  },
}));

interface CheckboxRequiredBoundsFormProps {
  maxRequired: number | null;
  minRequired: number;
  onMaxRequiredChange: (value: number | null) => void;
  onMinRequiredChange: (value: number) => void;
  optionCount: number;
  disabled?: boolean;
}

export function CheckboxRequiredBoundsForm({
  disabled = false,
  maxRequired,
  minRequired,
  onMaxRequiredChange,
  onMinRequiredChange,
  optionCount,
}: CheckboxRequiredBoundsFormProps): JSX.Element {
  const { classes } = useStyles();
  const minLabelId = useId();
  const maxLabelId = useId();
  const descriptionId = useId();

  useEffect(() => {
    const clamped = Queries.clampCheckboxRequiredBounds({
      maxRequired,
      minRequired,
      optionCount,
    });
    if (clamped.minRequired !== minRequired) onMinRequiredChange(clamped.minRequired);
    if (clamped.maxRequired !== maxRequired) onMaxRequiredChange(clamped.maxRequired);
  }, [maxRequired, minRequired, onMaxRequiredChange, onMinRequiredChange, optionCount]);

  /**
   * Keep the two bounds consistent based on which field the user just edited.
   * Raising min above max pulls max up to match; lowering max below min pulls
   * min down to match. The pure clamp can't tell which field changed, so without
   * this the "raise min above max" edit would be silently reverted.
   */
  function handleMinChange(value: number | null) {
    if (value === null) return;
    onMinRequiredChange(value);
    if (maxRequired !== null && value > maxRequired) onMaxRequiredChange(value);
  }

  function handleMaxChange(value: number | null) {
    onMaxRequiredChange(value);
    if (value !== null && value < minRequired) onMinRequiredChange(value);
  }

  return (
    <div className={classes.boundsColumn}>
      <div className={classes.boundsGrid}>
        <InputLabel className={classes.inputLabel} id={minLabelId}>
          Min
        </InputLabel>

        <InputLabel className={classes.inputLabel} id={maxLabelId}>
          Max
        </InputLabel>

        <BoundsSelect
          aria-describedby={descriptionId}
          aria-labelledby={minLabelId}
          disabled={disabled}
          name="min"
          options={getMinOptions(optionCount)}
          value={minRequired}
          onChange={handleMinChange}
        />

        <BoundsSelect
          aria-describedby={descriptionId}
          aria-labelledby={maxLabelId}
          disabled={disabled}
          name="max"
          options={getMaxOptions(optionCount)}
          value={maxRequired}
          onChange={handleMaxChange}
        />
      </div>

      <Typography color="text.secondary" id={descriptionId} variant="caption">
        Set how many options signers must select at minimum and at most.
      </Typography>
    </div>
  );
}

interface BoundsSelectProps {
  'aria-describedby': string;
  'aria-labelledby': string;
  disabled: boolean;
  name: 'min' | 'max';
  onChange: (value: number | null) => void;
  options: { label: string; value: number | null }[];
  value: number | null;
}

function BoundsSelect({
  name,
  onChange,
  options,
  value,
  ...props
}: BoundsSelectProps): JSX.Element {
  const [selectOpen, setSelectOpen] = useState(false);
  const selectedOption = options.find(option => option.value === value) ?? options[0];

  return (
    <ToolbarSelect
      {...props}
      MenuProps={{
        anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
        transformOrigin: { horizontal: 'right', vertical: 'top' },
      }}
      customRenderValue={selectedOption?.label ?? ''}
      input={<ToolbarInput fullWidth open={selectOpen} />}
      menuAttribute={`checkbox-${name}-required-menu`}
      open={selectOpen}
      spanAttribute={`checkbox-${name}-required-span`}
      value={value ?? ''}
      onClose={() => setSelectOpen(false)}
      onOpen={() => setSelectOpen(true)}
      onSelectChange={e => {
        const next = e.target.value;
        onChange(next === '' ? null : Number(next));
      }}
    >
      {options.map(option => (
        <MenuItem key={String(option.value)} value={option.value ?? ''}>
          {option.label}
        </MenuItem>
      ))}
    </ToolbarSelect>
  );
}

function getMinOptions(optionCount: number): { label: string; value: number }[] {
  return Array.from({ length: optionCount + 1 }, (_, value) => ({
    label: String(value),
    value,
  }));
}

function getMaxOptions(optionCount: number): { label: string; value: number | null }[] {
  return [
    { label: 'No maximum', value: null },
    ...Array.from({ length: optionCount }, (_, index) => {
      const value = index + 1;
      return { label: String(value), value };
    }),
  ];
}
